/*
 * Theme-wide cart increment guard.
 *
 * Wraps window.fetch to intercept /cart/add, /cart/change and /cart/update requests
 * and blocks them when the resulting quantity for a variant would either:
 *   - exceed the increment-floored available stock (limit.max), or
 *   - not be a whole multiple of the variant's increment (limit.increment).
 *
 * Per-variant limits are provided by Liquid via window.MFIncrementLimits
 * (see snippets/increment-limit-data.liquid). Variants without a limit are ignored.
 */
(function () {
  const ADD = /\/cart\/add(\.js)?(\?|$)/;
  const CHANGE = /\/cart\/change(\.js)?(\?|$)/;
  const UPDATE = /\/cart\/update(\.js)?(\?|$)/;

  const strings = window.cartIncrementStrings || {};
  const messages = {
    max: strings.max || "The maximum available quantity for this item is already in your cart.",
    multiple: strings.multiple || "This item can only be purchased in set multiples.",
  };

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function (resource, init) {
    try {
      const url = typeof resource === "string" ? resource : resource && resource.url;
      const method = (
        (init && init.method) ||
        (typeof resource !== "string" && resource && resource.method) ||
        "GET"
      ).toUpperCase();

      if (
        url &&
        method === "POST" &&
        (ADD.test(url) || CHANGE.test(url) || UPDATE.test(url))
      ) {
        const body = init && init.body;
        const message = await validate(url, body);
        if (message) {
          return errorResponse(message);
        }
      }
    } catch (e) {
      // Never let the guard break a legitimate request.
    }
    return originalFetch(resource, init);
  };

  function limits() {
    return window.MFIncrementLimits || {};
  }

  async function validate(url, body) {
    const data = parseBody(body);
    if (!data) {
      return null;
    }

    // Only fetch the live cart when we actually need current quantities (adds).
    let cart = null;
    const getCart = async () => {
      if (!cart) {
        const res = await originalFetch(`${(window.routes && window.routes.cart_url) || "/cart"}.js`);
        cart = await res.json();
      }
      return cart;
    };

    const currentQty = (variantId, loadedCart) =>
      loadedCart.items
        .filter((item) => item.id === Number(variantId))
        .reduce((total, item) => total + item.quantity, 0);

    if (ADD.test(url)) {
      const items = data.items ? data.items : [{ id: data.id, quantity: data.quantity }];
      const loadedCart = await getCart();
      for (const item of items) {
        if (!item || !item.id) continue;
        const resulting = currentQty(item.id, loadedCart) + Number(item.quantity || 1);
        const message = check(item.id, resulting);
        if (message) return message;
      }
      return null;
    }

    if (CHANGE.test(url)) {
      let variantId = data.id;
      if (!variantId && data.line) {
        const loadedCart = await getCart();
        variantId = loadedCart.items[Number(data.line) - 1] && loadedCart.items[Number(data.line) - 1].id;
      }
      return check(variantId, Number(data.quantity));
    }

    if (UPDATE.test(url) && data.updates) {
      if (Array.isArray(data.updates)) {
        const loadedCart = await getCart();
        for (let i = 0; i < data.updates.length; i++) {
          const variantId = loadedCart.items[i] && loadedCart.items[i].id;
          const message = check(variantId, Number(data.updates[i]));
          if (message) return message;
        }
      } else {
        for (const [variantId, quantity] of Object.entries(data.updates)) {
          const message = check(variantId, Number(quantity));
          if (message) return message;
        }
      }
    }

    return null;
  }

  // Returns an error message when the resulting quantity is invalid, otherwise null.
  function check(variantId, resulting) {
    if (!variantId || !Number.isFinite(resulting)) {
      return null;
    }

    const limit = limits()[Number(variantId)];
    if (!limit) {
      return null;
    }

    if (resulting <= 0) {
      return null; // removals are always allowed
    }

    if (limit.max != null && resulting > limit.max) {
      return messages.max;
    }

    if (limit.increment > 1 && resulting % limit.increment !== 0) {
      return messages.multiple;
    }

    return null;
  }

  function parseBody(body) {
    if (!body) {
      return null;
    }
    if (typeof body === "string") {
      try {
        return JSON.parse(body);
      } catch (e) {
        return formToObject(new URLSearchParams(body));
      }
    }
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      return formToObject(body);
    }
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      return formToObject(body);
    }
    return null;
  }

  function formToObject(entries) {
    const obj = {};
    entries.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  function errorResponse(message) {
    const payload = JSON.stringify({
      status: 422,
      message: message,
      description: message,
      errors: message,
    });
    return new Response(payload, {
      status: 422,
      headers: { "Content-Type": "application/json" },
    });
  }

})();
